import { useEffect, useRef } from 'react';
import { isSplitDefined } from '@/api/getSplitDetails';
import { useCheckoutContext } from '@/context/CheckoutContext/CheckoutContext';
import type { useSplitState } from '@/hooks/useSplitState';
import { sendBusinessEvent, setSplitBillContext } from '@/screens/Checkout/monitoring';
import { calculateTipToPay } from '@/utils/calculateTipToPay';

interface SplitAutoJoinParams {
  isSplitExist: boolean;
  splitDetails: ReturnType<typeof useSplitState>['splitDetails'];
}

/**
 * Sync a split established elsewhere (another payer's device, or this payer
 * returning after a redirect) into local checkout state, and send the joined
 * business event once per mount.
 *
 * Equal split: adopt the server's one-person share as this payer's amount.
 * Items split: lock the mode with an empty selection — the payer picks their
 * items via the picker; until then baseToPay is 0 and the payment widget
 * stays hidden.
 */
export const useSplitAutoJoin = ({ isSplitExist, splitDetails }: SplitAutoJoinParams) => {
  const {
    state: { numberOfSplits, splitType, selectedTipPercentage },
    actions: { initSplit, joinItemsSplit },
  } = useCheckoutContext();
  const splitJoinedSentRef = useRef(false);

  useEffect(() => {
    if (
      isSplitExist &&
      splitDetails &&
      isSplitDefined(splitDetails) &&
      splitDetails.type === 'SPLIT_EQUALLY' &&
      numberOfSplits === 0
    ) {
      const yourTip = calculateTipToPay(splitDetails.onePersonSplit, selectedTipPercentage);
      initSplit(splitDetails.numberOfSplits, 1, splitDetails.onePersonSplit, yourTip);

      if (!splitJoinedSentRef.current) {
        setSplitBillContext(splitDetails.numberOfSplits, 1);
        sendBusinessEvent('kitopay.split_bill.joined', {
          yourShare: splitDetails.onePersonSplit,
          remainingAmount: splitDetails.remainingAmount,
        });
        splitJoinedSentRef.current = true;
      }
    }
  }, [isSplitExist, splitDetails, numberOfSplits, selectedTipPercentage, initSplit]);

  useEffect(() => {
    if (
      isSplitExist &&
      isSplitDefined(splitDetails) &&
      splitDetails.type === 'SPLIT_BY_ITEMS' &&
      splitType === null
    ) {
      joinItemsSplit();

      if (!splitJoinedSentRef.current) {
        sendBusinessEvent('kitopay.split_bill.joined', {
          split_type: 'split_by_items',
          remainingAmount: splitDetails.remainingAmount,
        });
        splitJoinedSentRef.current = true;
      }
    }
  }, [isSplitExist, splitDetails, splitType, joinItemsSplit]);
};
